import React from 'react';

import { classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { FlexBox, Size, Message, Orientation, Alignment } from '@lumapps/lumx/react';
import {
    isSectionNode,
    NavigationItemType,
    NavigationMapItem,
    NavigationMapItemPermissionsType,
} from '@lumapps/navigation/types';
import { useTranslate } from '@lumapps/translations';
import { moveInDirection } from '@lumapps/utils/array/moveInDirection';
import { Direction } from '@lumapps/utils/types/Direction';

import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { useNavigationListFilters } from '../../context/NavigationListFiltersContext';
import { NAVIGATION_ADMIN } from '../../keys';
import { DEFAULT_ENABLED_OPTIONS, NavigationItemWizardOptionProps } from '../../types';
import { isNavigationItemFiltered, isSameNode } from '../../utils';
import { nodeIsEditable, nodeIsHidden } from '../../utils/nodes';
import { AddNavigationItem } from '../AddNavigationItem';
import { NavigationItemModalProps } from '../NavigationItemModal/NavigationItemModal';
import { NavigationListFilters } from '../NavigationListFilters';
import { NavigationListNode } from '../NavigationListNode';
import { EmptyFilteredState } from './EmptyFilteredState';

import './index.scss';

export interface NavigationListProps {
    /** CSS class name. */
    className?: string;
    /** Children nodes. */
    nodes: NavigationMapItem[];
    /** Navigation ID. */
    id?: string;
    /** Node depth. */
    depth?: number;
    /** Parent node path in navigation map. */
    parentPath?: string[];
    /** Callback on "delete" button clicked. */
    onDeleteNode?(nodeId: string[], buttonRef: React.Ref<HTMLElement>): void;
    /** Callback on add button clicked. */
    onAddNode?(path: string[], node: NavigationMapItem, buttonRef: React.Ref<HTMLElement>): void;
    /** Callback on edit button clicked. */
    onEditNode?(path: string[], node: NavigationMapItem, buttonRef?: React.Ref<HTMLElement>): void;
    /** Callback that will be executed when several nodes are changed at once */
    onReplaceNodes?(path: string[], nodes: NavigationMapItem[]): void;
    /** Callback to be executed when an attempt to move an item is made */
    onMoveNodeToSection?(node: NavigationMapItem, buttonRef: React.Ref<HTMLElement>): void;
    /** Maximum depth allowed for navigation items. */
    maxDepthAllowed?: number;
    /** Maximum amount of items allowed on a single level */
    maxWidthAllowed?: number;
    /** Amount of items allowed on a single level before showing the warning message */
    widthWarning?: number;
    /** the user's current language */
    currentLanguage: string;
    /** list of languages for the platform */
    languages: string[];
    /** Message to display once the depth limit has been reached */
    depthLimitReachedMessage?: string;
    /** Message to display once the width limit has been reached */
    widthLimitReachedMessage?: string;
    /** Message to display once the width warning limit has been reached */
    widthWarningReachedMessage?: string;
    /** Message to display once there is only one node in a level and therefore the node cannot be moved or deleted */
    oneNodeLimitReachedMessage?: string;
    /** Message to display when a user tries to move a node to a section they don't have permission to move in  */
    moveNodeRestrictionMessage?: string;
    /** scope to be used for tracking purposes */
    scope: string;
    /** Options to be passed into wizard */
    wizardOptions?: NavigationItemWizardOptionProps['options'];
    /** Override default parameters for all NavigationItemModals */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
    /** The ID of the Node or NavigationMapItem (section) we want to have the add item dialog pre-opened */
    addItemTargetId?: string;
    /** Callback to be sent when user open the modal */
    onToggleWizard?(isOpen: boolean): void;
    /** List of root section ID's */
    rootSectionIds?: string[];
    /** Whether the list has been set as filtered or not */
    showAsFiltered?: boolean;
    /** The user permission for current level */
    sectionPermission?: NavigationMapItemPermissionsType;
    /** Whether the nodes list is inside a Section node */
    isInsideSection: boolean;
}

const CLASSNAME = 'navigation-list';

export const NavigationList: React.FC<NavigationListProps> = ({
    id,
    className,
    depth = 1,
    nodes,
    parentPath = ['children'],
    onDeleteNode,
    onAddNode,
    onEditNode,
    onReplaceNodes,
    onMoveNodeToSection,
    maxDepthAllowed,
    maxWidthAllowed,
    widthWarning,
    currentLanguage,
    depthLimitReachedMessage,
    widthLimitReachedMessage,
    widthWarningReachedMessage,
    oneNodeLimitReachedMessage,
    moveNodeRestrictionMessage,
    scope,
    languages,
    wizardOptions = DEFAULT_ENABLED_OPTIONS,
    navigationItemModalProps = {},
    addItemTargetId,
    onToggleWizard,
    rootSectionIds,
    showAsFiltered: propShowAsFiltered,
    sectionPermission,
    isInsideSection,
}) => {
    const { get } = useDataAttributes(`${scope}-list`);
    const { translateKey } = useTranslate();
    const { filters, hideFilteredItems, itemsAreFiltered } = useNavigationListFilters();
    const { hasFilters, availableFilters, defaultFeeds, isListNode, shouldOpenEmptySectionsOnInit } =
        useNavigationAdminConfiguration();

    /**
     * Array of the remaining nodes after the filters are applied.
     *
     * As we can't actually filter the nodes to render, because it will break moving and editing,
     * the nodes will only be visually hidden.
     * Therefore, we need to track what nodes are filtered or not to display empty states for example.
     */
    const visibleNodes = React.useMemo(
        () =>
            nodes.filter((node) => {
                const isFiltered =
                    propShowAsFiltered ||
                    isNavigationItemFiltered(node, filters, {
                        allFeedId: defaultFeeds?.allFeed?.id,
                        publicFeedId: defaultFeeds?.publicFeed?.id,
                    });
                return !isFiltered;
            }),
        [defaultFeeds?.allFeed?.id, defaultFeeds?.publicFeed?.id, filters, nodes, propShowAsFiltered],
    );

    const maxWidthReached = maxWidthAllowed ? nodes?.length >= maxWidthAllowed : false;
    const widthWarningReached = widthWarning ? nodes?.length >= widthWarning : false;
    const maxDepthReached = maxDepthAllowed ? depth >= maxDepthAllowed : false;
    const onlyOneNode = nodes?.length <= 1;
    const displayFilters = depth === 1 && hasFilters;
    /** Whether all nodes at this level have been hidden via filters */
    const allNodesAreHidden = hideFilteredItems && nodes.length > 0 && visibleNodes?.length === 0;

    /**
     * The messages informing that some nodes have been filtered at this level
     * * At root level, only display if visibility switch is enabled and at least one filter is set.
     * * At all other levels, display if all nodes are hidden.
     */
    const showFilteredNodesMessage = depth === 1 ? hideFilteredItems && itemsAreFiltered : allNodesAreHidden;

    const onMoveNode = (node: NavigationMapItem, direction: Direction) => {
        const nodePosition = nodes.indexOf(node);
        const { movedItems } = moveInDirection(
            nodes,
            nodePosition,
            direction,
            /**
             * Skip all nodes set in the array but that the user can't see
             */
            (node) =>
                // Skip nodes the user doesn't have management permission to
                nodeIsHidden(node.partialPermission) ||
                // Skip nodes the user has filtered and hidden
                (hideFilteredItems && !visibleNodes.find((visibleNode) => visibleNode.id === node.id)),
        );

        if (onReplaceNodes) {
            onReplaceNodes(parentPath, movedItems);
        }
    };

    /** Display a single Node and its children recursively */
    const getNode = (node: NavigationMapItem, nodeMapIndex: number, isInsideSection: boolean) => {
        let displayedNodes = nodes;
        let nodeDisplayedIndex = nodeMapIndex;

        /**
         * The current displayed index of the node.
         * If nodes aren't filtered, simply use the map index.
         */
        const nodeFilteredIndex = itemsAreFiltered
            ? visibleNodes.findIndex((visibleNode) => visibleNode.id === node.id)
            : nodeMapIndex;

        /**
         * The node is filtered if it doesn't appear in the list of visible nodes.
         */
        const showAsFiltered = nodeFilteredIndex === -1;

        /** If filtered nodes are hidden, the index must match the visible nodes. */
        if (hideFilteredItems) {
            displayedNodes = visibleNodes;
            nodeDisplayedIndex = nodeFilteredIndex;
        }

        /**
         * The path to the current node.
         * The node path must use the map index even when filtered as it is used
         * to apply changes on edit.
         */
        const nodePath = [...parentPath, `${nodeMapIndex}`];
        // Node id (or use node path for new nodes not saved in the backend).
        const nodeId = node.id || JSON.stringify(nodePath);

        // Common props to the node, whether simple node or list node
        const navigationListNodeCommonProps = {
            key: nodeId,
            node,
            onDeleteNode,
            onEditNode,
            path: nodePath,
            isFirst: nodeDisplayedIndex === 0,
            isLast: nodeDisplayedIndex === displayedNodes.length - 1,
            isInsideSection,
            onMoveNode,
            onMoveNodeToSection,
            rootSectionIds,
            scope,
            maxDepthAllowed,
            showAsFiltered,
            hide: showAsFiltered && hideFilteredItems,
        };

        if (isListNode?.(node)) {
            return (
                <NavigationListNode
                    {...navigationListNodeCommonProps}
                    open={
                        isSameNode(nodeId, addItemTargetId) ||
                        Boolean(!node.children?.length && shouldOpenEmptySectionsOnInit)
                    }
                >
                    <NavigationList
                        depth={depth + 1}
                        nodes={node.children || []}
                        id={nodeId}
                        addItemTargetId={addItemTargetId}
                        onDeleteNode={onDeleteNode}
                        onAddNode={onAddNode}
                        onEditNode={onEditNode}
                        onReplaceNodes={onReplaceNodes}
                        parentPath={[...nodePath, 'children']}
                        onMoveNodeToSection={onMoveNodeToSection}
                        currentLanguage={currentLanguage}
                        depthLimitReachedMessage={depthLimitReachedMessage}
                        widthLimitReachedMessage={widthLimitReachedMessage}
                        widthWarningReachedMessage={widthWarningReachedMessage}
                        oneNodeLimitReachedMessage={oneNodeLimitReachedMessage}
                        moveNodeRestrictionMessage={moveNodeRestrictionMessage}
                        languages={languages}
                        scope={scope}
                        maxWidthAllowed={maxWidthAllowed}
                        maxDepthAllowed={maxDepthAllowed}
                        widthWarning={widthWarning}
                        navigationItemModalProps={navigationItemModalProps}
                        onToggleWizard={onToggleWizard}
                        rootSectionIds={rootSectionIds}
                        wizardOptions={wizardOptions}
                        showAsFiltered={showAsFiltered}
                        sectionPermission={node.partialPermission}
                        // If current node is a section, the list of child nodes is inside a section
                        isInsideSection={isSectionNode(node)}
                    />
                </NavigationListNode>
            );
        }

        return <NavigationListNode {...navigationListNodeCommonProps} />;
    };

    return (
        <FlexBox
            {...get({ element: 'root' })}
            className={classnames(className, CLASSNAME)}
            orientation="vertical"
            gap={Size.big}
        >
            {widthWarningReached && widthWarningReachedMessage ? (
                <Message kind="warning" hasBackground>
                    {widthWarningReachedMessage}
                </Message>
            ) : null}

            {/* If the filtered nodes are hidden, we display a message to inform the user. */}
            {showFilteredNodesMessage ? (
                <Message kind="info" hasBackground>
                    {translateKey(NAVIGATION_ADMIN.SHOW_ONLY_VISIBLE_ACTIVATED_WARNING)}
                </Message>
            ) : null}

            {(displayFilters || nodeIsEditable(sectionPermission)) && (
                <FlexBox
                    className={`${CLASSNAME}__toolbar`}
                    vAlign={displayFilters ? Alignment.spaceBetween : Alignment.left}
                    orientation={Orientation.horizontal}
                    gap={Size.big}
                >
                    {/* Filters of the list, only on the first level. */}
                    {displayFilters && (
                        <NavigationListFilters languages={languages} displayedFilters={availableFilters} />
                    )}
                    <FlexBox vAlign="center" hAlign="center" gap="big">
                        {nodeIsEditable(sectionPermission) && onAddNode && (
                            <AddNavigationItem
                                isDisabled={maxWidthReached}
                                defaultLanguage={currentLanguage}
                                languages={languages}
                                scope={scope}
                                onAdded={(navItem, buttonRef) => {
                                    onAddNode(parentPath, navItem, buttonRef);
                                }}
                                options={{
                                    ...wizardOptions,
                                    [NavigationItemType.SECTION]: !maxDepthReached,
                                }}
                                beforeOptions={
                                    maxDepthReached && depthLimitReachedMessage ? (
                                        <Message
                                            kind="info"
                                            hasBackground
                                            className={`${CLASSNAME}__max-depth-reached-message`}
                                        >
                                            {depthLimitReachedMessage}
                                        </Message>
                                    ) : null
                                }
                                navigationItemModalProps={navigationItemModalProps}
                                isOpen={isSameNode(id, addItemTargetId)}
                                onToggleWizard={onToggleWizard}
                            />
                        )}
                    </FlexBox>
                </FlexBox>
            )}

            {maxWidthReached && widthLimitReachedMessage ? (
                <Message kind="info" hasBackground>
                    {widthLimitReachedMessage}
                </Message>
            ) : null}

            {onlyOneNode && oneNodeLimitReachedMessage ? (
                <Message kind="info" hasBackground>
                    {oneNodeLimitReachedMessage}
                </Message>
            ) : null}

            {nodes?.length > 0 && (
                <FlexBox
                    {...get({ element: 'wrapper' })}
                    className={`${CLASSNAME}__wrapper`}
                    orientation="vertical"
                    gap={Size.regular}
                >
                    {nodes?.map((node, index) => getNode(node, index, isInsideSection))}
                </FlexBox>
            )}
            {/**
             * If the user has requested to only show visible nodes and
             * all nodes has been filtered,
             * show an empty state
             */}
            {allNodesAreHidden && <EmptyFilteredState />}
        </FlexBox>
    );
};
NavigationList.displayName = 'NavigationList';
